import { useSyncExternalStore } from 'react';
import { authHeaders } from '@/lib/authHeaders';

/** "Seen" notification ids — cached locally for instant UI, SYNCED to the
 *  server per account so phone and desktop always agree. */
const KEY = 'lexidy-seen-notifs';
const listeners = new Set<() => void>();

/** Fire-and-forget push of read/unread changes to the server. */
function pushToServer(add: string[], remove: string[]) {
  void (async () => {
    try {
      await fetch('/api/account/notifs-seen', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(await authHeaders()) },
        body: JSON.stringify({ add, remove }),
      });
    } catch { /* offline is fine — local cache still applies */ }
  })();
}

/** Pull the account's seen ids once at app start: server state wins by UNION
 *  (anything read anywhere counts as read everywhere), and ids this browser
 *  read while the server didn't know are pushed up. */
export async function hydrateSeenFromServer(): Promise<void> {
  try {
    const r = await fetch('/api/account/notifs-seen', { headers: await authHeaders() });
    if (!r.ok) return;
    const server = new Set(((await r.json()) as { ids?: string[] }).ids || []);
    const local = read();
    const localOnly = [...local].filter((id) => !server.has(id));
    const merged = new Set([...server, ...local]);
    try { localStorage.setItem(KEY, JSON.stringify([...merged])); } catch { /* ignore */ }
    cache = merged;
    listeners.forEach((l) => l());
    if (localOnly.length) pushToServer(localOnly, []);
  } catch { /* ignore — local cache still applies */ }
}

function read(): Set<string> {
  try {
    return new Set(JSON.parse(localStorage.getItem(KEY) || '[]') as string[]);
  } catch {
    return new Set();
  }
}
let cache = read();

export function markSeen(ids: string[]) {
  const s = read();
  let changed = false;
  ids.forEach((i) => {
    if (!s.has(i)) {
      s.add(i);
      changed = true;
    }
  });
  if (!changed) return;
  try {
    localStorage.setItem(KEY, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
  cache = s;
  listeners.forEach((l) => l());
  pushToServer(ids, []);
}

/** Mark notifications back as unread (they return to the unread section live). */
export function markUnseen(ids: string[]) {
  const s = read();
  let changed = false;
  ids.forEach((i) => {
    if (s.has(i)) {
      s.delete(i);
      changed = true;
    }
  });
  if (!changed) return;
  try {
    localStorage.setItem(KEY, JSON.stringify([...s]));
  } catch {
    /* ignore */
  }
  cache = s;
  listeners.forEach((l) => l());
  pushToServer([], ids);
}

export function useSeen(): Set<string> {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => cache,
    () => cache,
  );
}
